import { useAuth } from "../contexts/AuthContext";
import { useCan } from "./useCan";

/**
 * RBAC permission hook. Derives permissions from /api/v1/me/access (via
 * AuthContext.access) and workspace role. Role→permission mapping mirrors
 * the backend Rbac module (app/models/concerns/rbac.rb).
 *
 * Usage:
 *   const { canWrite, canManageTeam, isViewer } = usePermissions();
 *   {canWrite && <button>Create Agent</button>}
 */
export const usePermissions = () => {
  const { access, currentWorkspace } = useAuth();

  // All useCan calls must be unconditional (Rules of Hooks).
  const canWriteAgent         = useCan('agent', 'write');
  const canWriteTeamMember    = useCan('team_member', 'write');
  const canWriteSubscription  = useCan('subscription_plan', 'write');
  const canWriteApiKey        = useCan('api_key', 'write');

  const isInternalAdmin = !!access?.internal_admin;
  const role = access?.role || currentWorkspace?.role;

  return {
    role:             isInternalAdmin ? 'owner' : role,
    isInternalAdmin,
    isOwner:          isInternalAdmin || role === 'owner',
    isAdmin:          isInternalAdmin || role === 'owner' || role === 'admin',
    isViewer:         !isInternalAdmin && role === 'viewer',
    canWrite:         canWriteAgent,
    canManageTeam:    canWriteTeamMember,
    canManageBilling: canWriteSubscription,
    canManageApiKeys: canWriteApiKey,
  };
};

export default usePermissions;
